import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  clearNatureR03Access,
  publishNatureR03Access,
  type NatureR03AccessSnapshot,
} from "./NatureR03AccessRegistry";
import { NatureR03LivePanel } from "./NatureR03LivePanel";

const LIVE_STYLE_ID = "thera-nature-r03-live-style";

function bridgeEnabled() {
  const value = String(import.meta.env.VITE_NATURE_R03_BRIDGE ?? "").trim().toLowerCase();
  return value === "1" || value === "true" || value === "on" || value === "lab" || value === "beta";
}

function readExistingArAccess(): NatureR03AccessSnapshot {
  const naturePanel = document.querySelector<HTMLElement>(".thera-ar-nature-panel");
  const cameraVideo = document.querySelector<HTMLVideoElement>('video[aria-label="THERA AR live camera"]');
  const srcObject = cameraVideo?.srcObject ?? null;
  const cameraStream = typeof MediaStream !== "undefined" && srcObject instanceof MediaStream ? srcObject : null;
  const cameraStatus = document.querySelector<HTMLElement>(".thera-ar-live-surface")?.dataset.cameraStatus ?? null;
  return { naturePanel, cameraVideo, cameraStream, cameraStatus };
}

function installLiveUiStyle() {
  if (document.getElementById(LIVE_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = LIVE_STYLE_ID;
  style.textContent = `
html[data-thera-nature-r03-ui="live"] .thera-ar-nature-panel > .thera-nature-r01 { display:none !important; }
.thera-nature-r03-live { display:grid; gap:10px; min-width:0; }
.thera-nature-r03-live-head { display:flex; align-items:center; justify-content:space-between; gap:10px; }
.thera-nature-r03-live-head > div { display:grid; gap:2px; }
.thera-nature-r03-live-head strong { font-size:.94rem; }
.thera-nature-r03-live-head small { font-size:.68rem; opacity:.7; }
.thera-nature-r03-dot { width:9px; height:9px; border-radius:999px; background:rgba(255,255,255,.26); box-shadow:0 0 0 4px rgba(255,255,255,.06); flex:0 0 auto; }
.thera-nature-r03-dot.ready { background:#7fbd5c; box-shadow:0 0 0 4px rgba(127,189,92,.16); }
.thera-nature-r03-live-help { margin:0; font-size:.72rem; line-height:1.4; opacity:.74; }
.thera-nature-r03-live-card { display:grid; gap:5px; padding:10px; border-radius:12px; background:rgba(255,255,255,.07); border:1px solid rgba(255,255,255,.08); }
.thera-nature-r03-live-card strong { font-size:.8rem; }
.thera-nature-r03-live-card span { font-size:.7rem; line-height:1.35; opacity:.82; }
.thera-nature-r03-live-card.error { border-color:rgba(255,116,116,.28); background:rgba(120,20,20,.16); }
.thera-nature-r03-live-card.result { border-color:rgba(127,189,92,.28); background:rgba(64,101,45,.15); }
.thera-nature-r03-kicker { text-transform:uppercase; letter-spacing:.05em; font-size:.62rem !important; opacity:.66 !important; }
.thera-nature-r03-species { font-size:1rem !important; font-style:italic; }
.thera-nature-r03-live-card ol { display:grid; gap:4px; margin:4px 0 0; padding:0; list-style:none; }
.thera-nature-r03-live-card li { display:flex; justify-content:space-between; gap:10px; font-size:.68rem; }
.thera-nature-r03-live-card li small { opacity:.66; font-variant-numeric:tabular-nums; }
.thera-nature-r03-disclaimer { margin-top:4px; font-size:.62rem !important; line-height:1.35; opacity:.58 !important; }
.thera-nature-r03-live-meta { display:flex; justify-content:space-between; gap:10px; font-size:.62rem; opacity:.58; }
`;
  document.head.appendChild(style);
}

export function NatureR03AppBridge() {
  const [liveHost, setLiveHost] = useState<HTMLElement | null>(null);
  const enabled = bridgeEnabled();

  useEffect(() => {
    if (!enabled) {
      clearNatureR03Access();
      setLiveHost(null);
      delete document.documentElement.dataset.theraNatureR03Access;
      delete document.documentElement.dataset.theraNatureR03Ui;
      document.getElementById(LIVE_STYLE_ID)?.remove();
      return;
    }

    installLiveUiStyle();
    document.documentElement.dataset.theraNatureR03Ui = "live";

    let stopped = false;
    let runtimeCleanup: (() => void) | undefined;

    const publish = () => {
      if (stopped) return;
      const snapshot = readExistingArAccess();
      publishNatureR03Access(snapshot);
      setLiveHost((current) => current === snapshot.naturePanel ? current : snapshot.naturePanel);
    };

    const observer = new MutationObserver(publish);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-camera-status"],
    });
    const timer = window.setInterval(publish, 750);
    publish();

    void import("./NatureR03RuntimeBootstrap").then(({ startNatureR03RuntimeAccessProbe }) => {
      if (stopped) return;
      runtimeCleanup = startNatureR03RuntimeAccessProbe();
    });

    return () => {
      stopped = true;
      observer.disconnect();
      window.clearInterval(timer);
      runtimeCleanup?.();
      clearNatureR03Access();
      setLiveHost(null);
      delete document.documentElement.dataset.theraNatureR03Access;
      delete document.documentElement.dataset.theraNatureR03Ui;
      document.getElementById(LIVE_STYLE_ID)?.remove();
    };
  }, [enabled]);

  if (!enabled || !liveHost) return null;
  return createPortal(<NatureR03LivePanel />, liveHost);
}
